import { queryKeys } from '@query/keys'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalProps } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Body1, Body2 } from '@ui/Typography'
import { Button, IconButton } from '@ui/components/Button'
import { Checkbox } from '@ui/components/Checkbox'
import { Link } from '@ui/components/Link'
import { Modal } from '@ui/components/Modal'
import { CloseIcon } from '@ui/icons/Close.icon'
import { ReplayIcon } from '@ui/icons/Replay.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { CommonPaths } from '@utils/path'

import { LuckyDrawCampaign, LuckyDrawRewardTypeResponse, Sector } from '@model/LuckyDrawDto'

import { useFundingNav } from '@hooks/useFundingNav'

import { Box } from '@shared/components/Box'

import { useDepositTracker } from '@analytics/amplitude/deposit'

import { WheelRef } from '../LuckyDraw'
import { DetailsSection, LuckyDrawTab } from './components/DetailsSection'
import { WheelSection } from './components/WheelSection'
import { HistoryTab } from './tabs/HistoryTab'
import { OverviewTab } from './tabs/OverviewTab'
import { PrizesTab } from './tabs/PrizesTab'

import styles from './LuckyDraw.modal.module.scss'

export interface LuckyDrawModalProps extends BaseModalProps {
  campaign: LuckyDrawCampaign
}

const CLOSE_BUTTON_OFFSET = '12px'

export const LuckyDrawModal: React.FC<LuckyDrawModalProps> = (props) => {
  const { campaign, close } = props
  const { navigateToFunding } = useFundingNav()
  const tracker = useDepositTracker()

  const handleDepositNavigate = () => {
    tracker.start({ entry_point: 'lucky_draw' })
    navigateToFunding(CommonPaths.Deposit)
    close()
  }

  const { t } = useTranslation()

  const isMobile = useWindowResize()
  const locale = useSessionLanguage()

  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  const fortuneWheelRef = useRef<WheelRef>(null)
  const [winner, setWinner] = useState<number>(2)
  const [playing, setPlaying] = useState<boolean>(false)
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false)
  const [won, setWon] = useState<boolean>(false)
  const [chances, setChances] = useState<number>(0)
  const [activeTab, setActiveTab] = useState<LuckyDrawTab>(LuckyDrawTab.Overview)
  const [sectors, setSectors] = useState<Sector[]>([])
  const [rewardTypes, setRewardTypes] = useState<Record<number, LuckyDrawRewardTypeResponse>>({})
  const [hasSpun, setHasSpun] = useState<boolean>(false)

  useEffect(() => {
    if (won) {
      setActiveTab(undefined as unknown as LuckyDrawTab)
      getChances()
    }
  }, [won])
  const { campaignId, minDeposit, maxReward } = campaign

  const getPrizes = async () => {
    const prizes = await apiClient.getLuckyDrawPrizes(campaignId, locale)

    if (prizes.length % 2) {
      prizes.push({
        id: 'no-prize',
        reward: t('luckyDraw.noPrize'),
        campaignId,
        number: prizes.length + 1,
      })
    }
    setSectors(prizes)
  }

  const getLuckyDrawRewardTypes = async () => {
    const rewardTypesData = await apiClient.getLuckyDrawRewardTypes(locale)
    const rewardTypesMap = rewardTypesData.reduce(
      (acc: Record<number, LuckyDrawRewardTypeResponse>, type: LuckyDrawRewardTypeResponse) => {
        acc[type.id] = type
        return acc
      },
      {}
    )

    setRewardTypes(rewardTypesMap)
  }

  const getChances = async () => {
    setChances(await apiClient.getLuckyDrawChances(campaignId))
  }

  const spinTheWheel = async () => {
    setWon(false)
    setPlaying(true)
    setActiveTab(LuckyDrawTab.Prizes)
    const prize = await apiClient.getLuckDrawPrize(campaignId)
    setWinner(sectors?.findIndex((sector) => sector.id === prize.id))

    queryClient.invalidateQueries({
      queryKey: queryKeys.luckyDrawHistory(campaignId, locale),
    })

    fortuneWheelRef?.current?.startSpinning()
  }

  useEffect(() => {
    getPrizes()
    getChances()
    getLuckyDrawRewardTypes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClose = () => {
    if (playing) {
      return
    } else {
      close()
    }
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case LuckyDrawTab.Overview:
        return <OverviewTab minDeposit={minDeposit} maxReward={maxReward} />
      case LuckyDrawTab.Prizes:
        return <PrizesTab sectors={sectors} rewardTypes={rewardTypes} />
      case LuckyDrawTab.History:
        return <HistoryTab campaignId={campaignId} />
      default:
        return null
    }
  }

  const ModalFooter = () =>
    !(hasSpun || won) ? (
      <Modal.Footer>
        <Modal.Footer.Top>
          <div
            className={classNames(styles.checkboxSlot, { [styles.checkboxHidden]: playing })}
            aria-hidden={playing}
          >
            <Checkbox size='md' checked={termsAccepted} onChange={setTermsAccepted}>
              <Body2 color='text-med-em'>
                {t('luckyDraw.newAgree')}{' '}
                <Link
                  size='md'
                  variant='interactive'
                  href={campaign.termsAndConditionsUrl}
                  target={'_blank'}
                >
                  {t('luckyDraw.termsConditions')}
                </Link>
              </Body2>
            </Checkbox>
          </div>
        </Modal.Footer.Top>
        {chances > 0 ? (
          <Modal.Footer.WithSide
            text={t('luckyDraw.spinsLeft', { count: chances })}
            icon={ReplayIcon}
          >
            <Modal.Footer.Brand
              onClick={spinTheWheel}
              disabled={!termsAccepted || !chances || playing}
            >
              {t('luckyDraw.buttons.spin')}
            </Modal.Footer.Brand>
          </Modal.Footer.WithSide>
        ) : (
          <Modal.Footer.Brand disabled={!termsAccepted} onClick={handleDepositNavigate}>
            {t('luckyDraw.buttons.deposit')}
          </Modal.Footer.Brand>
        )}
      </Modal.Footer>
    ) : (
      <Modal.Footer>
        {chances > 0 ? (
          <>
            <Modal.Footer.Top>
              <Body1 color='text-med-em'>{t('luckyDraw.spinsLeftAgain', { count: chances })}</Body1>
            </Modal.Footer.Top>

            <div className={styles.footerActions}>
              <Button.Brand
                fullWidth
                size='lg'
                onClick={spinTheWheel}
                disabled={!termsAccepted || !chances || playing}
              >
                {t('luckyDraw.buttons.spin')}
              </Button.Brand>
              <Button.Secondary size='lg' onClick={handleClose} disabled={playing} fullWidth>
                {t('luckyDraw.buttons.notNow')}
              </Button.Secondary>
            </div>
          </>
        ) : (
          <>
            <Modal.Footer.Top>
              <Body1 color='text-med-em'>{t('luckyDraw.noSpinsLeft')}</Body1>
            </Modal.Footer.Top>

            <Modal.Footer.Brand disabled={!termsAccepted} onClick={handleDepositNavigate}>
              {t('luckyDraw.buttons.deposit')}
            </Modal.Footer.Brand>
          </>
        )}
      </Modal.Footer>
    )

  return (
    <Modal.FullScreen size='xl'>
      <div className={styles.coinsDecoration} aria-hidden='true' />
      {isMobile && (
        <div className={styles.mobileHeaderSpacing}>
          <Modal.Header>{t('luckyDraw.title')}</Modal.Header>
        </div>
      )}
      {!isMobile && (
        <Box
          position='absolute'
          style={{ insetInlineEnd: CLOSE_BUTTON_OFFSET, top: CLOSE_BUTTON_OFFSET }}
        >
          <IconButton.Secondary
            size='sm'
            aria-label='Close modal'
            onClick={handleClose}
            icon={{ icon: CloseIcon, color: 'text-med-em' }}
          />
        </Box>
      )}
      <Modal.Grid.Row layout='1-1' noGap>
        <Modal.Grid.Column noPadding>
          <WheelSection
            isMobile={isMobile}
            sectors={sectors}
            winner={winner}
            fortuneWheelRef={fortuneWheelRef}
            setPlaying={setPlaying}
            setWon={setWon}
            onSpinComplete={() => setHasSpun(true)}
          />
        </Modal.Grid.Column>
        <Modal.Grid.Column>
          <Modal.Body isScrollHintNeeded>
            <Box flex direction='column' alignSelf='stretch' className={styles.detailsWrapper}>
              <DetailsSection
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                campaign={campaign}
                won={won}
                onClose={handleClose}
                sectors={sectors}
                winner={winner}
                setWon={setWon}
                isSpinning={playing}
              >
                {renderTabContent()}
              </DetailsSection>
            </Box>
          </Modal.Body>
          {<ModalFooter />}
        </Modal.Grid.Column>
      </Modal.Grid.Row>
    </Modal.FullScreen>
  )
}
